import { isImageDocument, isPdfDocument } from '@apis/documents';
import { exportMimeFor } from './utils/markupTypes';
import { generatePath } from 'react-router-dom';
import { ANNOTATIONS_SITE_ROUTE } from '@src/routePatterns';

/**
 * Which files the page can open: the ones it can hand back as one annotated
 * file. Approval status doesn't appear — the backend hides a version the caller
 * may not see, on read and on write.
 */
export function canAnnotate(docName: string, fileId: string): boolean {
  const doc = { name: docName, id: fileId };
  if (isPdfDocument(doc)) return true;
  // The app must be able to hand the file back as itself with the marks in it.
  // SVG, GIF and BMP render but cannot be re-encoded, so they are not offered.
  return isImageDocument(doc) && exportMimeFor(docName || fileId) !== '';
}

/** Built from the route it is registered under; the file and version are the
 *  state, so the URL is shareable and reload-safe. */
export function annotationsPath(siteId: string, fileId: string, version: number | null): string {
  const v = version === null ? '' : `&v=${version}`;
  return `${generatePath(ANNOTATIONS_SITE_ROUTE, { siteId })}?file=${encodeURIComponent(fileId)}${v}`;
}

/** The display name, which the page cannot derive: a rename leaves the
 *  physical name in the file id. */
/** Why the page is not ready to be drawn on or taken away yet. */
export type AnnotationsBlocker = 'document' | 'annotations' | 'annotationsFailed' | 'failed';

export interface IAnnotationsNavState {
  docName?: string;
  /** What "Original file" should save as — the caller knows which version the
   *  document currently stands for, so it can mark a superseded one. Without it
   *  the page falls back to a fresh download, which is named by the server. */
  downloadName?: string;
}
